import React, { useState, useRef } from 'react';
import { FaUpload, FaImage } from 'react-icons/fa';
import './DragDropImageUpload.css';

const DragDropImageUpload = ({ 
  onImageSelect, 
  maxFiles = 10,
  acceptedFileTypes = ['image/jpeg', 'image/png', 'image/webp'],
  disabled = false 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;
    
    // Increment counter when cursor enters the component or any of its children
    dragCounter.current += 1;
    
    if (dragCounter.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;
    
    // Decrement counter when cursor leaves the component or any of its children
    dragCounter.current -= 1;
    
    // Only set dragging to false when cursor completely leaves the component
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // No state changes needed here - just prevent default browser behavior
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;
    
    // Reset counter and dragging state
    dragCounter.current = 0;
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Filter files by accepted types
      const validFiles = Array.from(e.dataTransfer.files).filter(file => 
        acceptedFileTypes.includes(file.type.toLowerCase())
      );

      if (validFiles.length === 0) {
        alert('Please upload only supported image formats: ' + 
          acceptedFileTypes.map(type => type.split('/')[1].toUpperCase()).join(', '));
        return;
      }

      // Create a synthetic event to pass to the handler
      const event = {
        target: {
          files: validFiles
        }
      };
      onImageSelect(event);
      e.dataTransfer.clearData();
    }
  };

  const handleClick = (e) => {
    if (disabled) return;
    
    // Prevent propagation to avoid conflicts with other click handlers
    e.stopPropagation();
    fileInputRef.current.click();
  };

  return (
    <div 
      className={`drag-drop-upload ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input 
        type="file"
        ref={fileInputRef}
        className="file-input"
        multiple
        accept={acceptedFileTypes.join(',')}
        onChange={onImageSelect}
        disabled={disabled}
        style={{ display: 'none' }} // Hide the actual input
      />
      
      <div className="upload-content">
        <FaUpload className="upload-icon" />
        <div className="upload-text">
          <h4>Drop images here</h4>
          <p>or click to browse files</p>
          <small>Upload up to {maxFiles} images ({acceptedFileTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')})</small>
        </div>
      </div>
      
      {isDragging && (
        <div className="drop-zone-overlay">
          <FaImage className="drop-icon" />
          <p>Release to upload files</p>
        </div>
      )}
    </div>
  );
};

export default DragDropImageUpload; 